import { createFileRoute, Link } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import {
  BIBLE_BOOKS,
  type BibleBook,
  biblePath,
  booksOf,
  GENRE_LABEL,
} from "../lib/bible";
import {
  breadcrumbJsonLd,
  canonical,
  collectionJsonLd,
  documentHeaders,
  socialMeta,
} from "../lib/seo";

const TITLE = "Every book of the Bible, in one page each — Bible Shield";
const DESCRIPTION =
  "All 66 books: who wrote each one, what it is actually doing, which chapter to start at, and the thing readers most often get wrong about it.";

/**
 * `/bible` — the hub for the sixty-six.
 *
 * ── Why the whole canon and not the popular half ─────────────────────────────
 *
 * Because a hub listing sixty-six books where only forty have pages is worse
 * than either alternative: it advertises the gaps. And the searches this
 * section exists for are distributed across the whole canon — "book of Habakkuk
 * summary" is a small query and it is a query, and nobody else is answering it
 * for a reader who wants to know where to start rather than a commentary.
 */
export const Route = createFileRoute("/bible/")({
  headers: documentHeaders,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...socialMeta({
        title: TITLE,
        description: DESCRIPTION,
        path: "/bible",
        lang: "en",
      }),
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Bible Shield", path: "/" },
          { name: "Books of the Bible", path: "/bible" },
        ]),
      },
      {
        "script:ld+json": collectionJsonLd({
          name: TITLE,
          description: DESCRIPTION,
          path: "/bible",
          items: BIBLE_BOOKS.map((b) => ({
            name: `The book of ${b.name}`,
            path: biblePath(b.slug),
          })),
        }),
      },
    ],
    links: [{ rel: "canonical", href: canonical("/bible") }],
  }),
  component: BibleIndex,
});

function BookList({ books }: { books: BibleBook[] }) {
  return (
    <ul className="mt-6 grid gap-3 sm:grid-cols-2">
      {books.map((book) => (
        <li key={book.slug}>
          <Link
            to="/bible/$slug"
            params={{ slug: book.slug }}
            className="brutal flex h-full items-baseline gap-3 p-4 transition-transform hover:-translate-y-0.5"
          >
            <span className="font-display text-lg font-bold text-ink">
              {book.name}
            </span>
            <span className="ml-auto font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
              {book.chapters} ch · {GENRE_LABEL[book.genre]}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function BibleIndex() {
  return (
    <PageChrome>
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        books of the bible
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        Sixty-six books, and where to start in each
      </h1>
      <p className="mt-4 max-w-[60ch] text-lg leading-relaxed text-ink2">
        Most reference pages will tell you a book's author and chapter count.
        These do that and then answer the question people actually have: which
        chapter to open first, and what the book is usually assumed to be about
        that it is not.
      </p>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-ink">
          Old Testament
        </h2>
        <BookList books={booksOf("old")} />
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-ink">
          New Testament
        </h2>
        <BookList books={booksOf("new")} />
      </section>
    </PageChrome>
  );
}
